import axios, { AxiosInstance } from 'axios';
import { authStorage } from './authStorage';

type AuthHandlers = {
  onUnauthorized: () => void;
  onPasswordChangeRequired: () => void;
};

let authHandlers: AuthHandlers | null = null;

export const setApiAuthHandlers = (handlers: AuthHandlers) => {
  authHandlers = handlers;
};

const LOCAL_API_BASE_URL = 'http://127.0.0.1:8080';
const PROD_API_BASE_URL = 'https://geofleetepro.com';

type BrowserLocation = {
  origin?: string;
  protocol?: string;
  hostname?: string;
};

const browserLocation = (): BrowserLocation | undefined => {
  if (typeof globalThis !== 'object' || !('location' in globalThis)) {
    return undefined;
  }

  return (globalThis as { location?: BrowserLocation }).location;
};

const normalizeApiBaseUrl = (value?: string): string | undefined => {
  const trimmedValue = value?.trim();

  if (!trimmedValue) {
    return undefined;
  }

  return trimmedValue.replace(/\/+$/, '');
};

const getFallbackApiBaseUrl = (): string => {
  const location = browserLocation();
  const hostname = location?.hostname;

  if (hostname) {
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return LOCAL_API_BASE_URL;
    }

    // The public site is served by the reverse proxy, which forwards /api to the backend.
    return location?.origin ?? '';
  }

  if (typeof __DEV__ !== 'undefined' && __DEV__) {
    return LOCAL_API_BASE_URL;
  }

  return PROD_API_BASE_URL;
};

const resolveApiBaseUrl = (): string => {
  const configured = normalizeApiBaseUrl(process.env.EXPO_PUBLIC_API_BASE_URL);
  const location = browserLocation();

  // An HTTPS page cannot call the baked-in http://ip:8080 address.
  if (location?.protocol === 'https:' && (!configured || configured.startsWith('http://'))) {
    return location.origin ?? '';
  }

  return configured ?? getFallbackApiBaseUrl();
};

export const API_BASE_URL = resolveApiBaseUrl();

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
});

api.interceptors.request.use(async (config) => {
  const token = await authStorage.getToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const url = String(error.config?.url ?? '');
    const erreur = error.response?.data?.erreur;

    if (status === 401 && !url.includes('/api/auth/login')) {
      await authStorage.clear();
      authHandlers?.onUnauthorized();
    }

    if (status === 403 && typeof erreur === 'string' && erreur.toLowerCase().includes('mot de passe')) {
      const current = await authStorage.load();
      if (current.token) {
        await authStorage.save({ ...current, mustChangePassword: true });
      }
      authHandlers?.onPasswordChangeRequired();
    }

    return Promise.reject(error);
  }
);

export default api;
